import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface UploadProgressState {
  isUploading: boolean;
  totalCount: number;
  completedCount: number;
  failedCount: number;
  mediaType: 'video' | 'music' | 'photo' | 'mixed';
  individualProgress: Record<string, number>;
}

interface UploadProgressContextType extends UploadProgressState {
  startUpload: (totalCount: number, mediaType?: 'video' | 'music' | 'photo' | 'mixed') => void;
  addToUpload: (count: number) => void;
  updateProgress: (fileId: string, progress: number) => void;
  completeFile: () => void;
  failFile: () => void;
  discardFile: () => void;
  resetUpload: () => void;
}

const UploadProgressContext = createContext<UploadProgressContextType | undefined>(undefined);

export function UploadProgressProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<UploadProgressState>({
    isUploading: false,
    totalCount: 0,
    completedCount: 0,
    failedCount: 0,
    mediaType: 'mixed',
    individualProgress: {},
  });

  const startUpload = useCallback((totalCount: number, mediaType: 'video' | 'music' | 'photo' | 'mixed' = 'mixed') => {
    setState({
      isUploading: true,
      totalCount,
      completedCount: 0,
      failedCount: 0,
      mediaType,
      individualProgress: {},
    });
  }, []);

  const addToUpload = useCallback((count: number) => {
    setState((prev) => ({
      ...prev,
      isUploading: true,
      totalCount: prev.totalCount + count,
    }));
  }, []);

  const updateProgress = useCallback((fileId: string, progress: number) => {
    setState((prev) => ({
      ...prev,
      individualProgress: {
        ...prev.individualProgress,
        [fileId]: progress,
      },
    }));
  }, []);

  const completeFile = useCallback(() => {
    setState((prev) => ({
      ...prev,
      completedCount: prev.completedCount + 1,
      isUploading: prev.completedCount + 1 + prev.failedCount < prev.totalCount,
    }));
  }, []);

  const failFile = useCallback(() => {
    setState((prev) => ({
      ...prev,
      failedCount: prev.failedCount + 1,
      isUploading: prev.completedCount + prev.failedCount + 1 < prev.totalCount,
    }));
  }, []);

  const discardFile = useCallback(() => {
    setState((prev) => {
      const newTotal = Math.max(0, prev.totalCount - 1);
      const isDone = prev.completedCount + prev.failedCount >= newTotal;
      return {
        ...prev,
        totalCount: newTotal,
        isUploading: newTotal > 0 && !isDone,
      };
    });
  }, []);

  const resetUpload = useCallback(() => {
    setState({
      isUploading: false,
      totalCount: 0,
      completedCount: 0,
      failedCount: 0,
      mediaType: 'mixed',
      individualProgress: {},
    });
  }, []);

  return (
    <UploadProgressContext.Provider
      value={{
        ...state,
        startUpload,
        addToUpload,
        updateProgress,
        completeFile,
        failFile,
        discardFile,
        resetUpload,
      }}
    >
      {children}
    </UploadProgressContext.Provider>
  );
}

export function useUploadProgress() {
  const context = useContext(UploadProgressContext);
  if (!context) {
    throw new Error('useUploadProgress must be used within UploadProgressProvider');
  }
  return context;
}
